import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { getScrollEventTarget, getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {HTMLElement | null} */
  #buyButtonsBlock = null;
  /** @type {Element | null} */
  #footer = null;
  /** @type {Element | null} */
  #visibilityRoot = null;
  /** @type {EventTarget | null} */
  #scrollEventTarget = null;
  /** @type {number | null} */
  #visibilityFrame = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  #variantUpdateVersion = 0;
  #variantUpdating = false;
  #variantUpdateFailed = false;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #variantUpdateTimeout;

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();

    this.#setupIntersectionObserver();
    this.refs.stickyBar.inert = this.refs.stickyBar.dataset.stuck !== 'true';

    const { signal } = this.#abortController;
    scrollContainerMediaQuery.addEventListener('change', this.#handleScrollContainerChange, { signal });
    window.addEventListener('resize', this.#scheduleVisibilityCheck, { signal, passive: true });
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
    if (this.#variantUpdateFailed) this.#setVariantState('error');

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#scrollEventTarget?.removeEventListener('scroll', this.#scheduleVisibilityCheck);
    if (this.#visibilityFrame !== null) cancelAnimationFrame(this.#visibilityFrame);
    this.#visibilityFrame = null;
    this.#abortController.abort();
    ++this.#variantUpdateVersion;
    clearTimeout(this.#variantUpdateTimeout);
    if (this.#variantUpdating) this.#variantUpdateFailed = true;
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#scrollEventTarget?.removeEventListener('scroll', this.#scheduleVisibilityCheck);
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    this.#buyButtonsBlock = buyButtonsBlock;
    this.#footer = footer;
    this.#visibilityRoot = getIntersectionRoot();
    // A fast scroll may skip the entire block between observer samples
    // (not intersecting before OR after). Scroll and IO share one measurement.
    this.#buyButtonsIntersectionObserver = new IntersectionObserver(this.#scheduleVisibilityCheck, {
      root: this.#visibilityRoot,
    });
    this.#mainBottomObserver = new IntersectionObserver(this.#scheduleVisibilityCheck, {
      root: this.#visibilityRoot,
      rootMargin: '200px 0px 0px 0px',
    });

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    this.#scrollEventTarget = getScrollEventTarget();
    this.#scrollEventTarget.addEventListener('scroll', this.#scheduleVisibilityCheck, { passive: true });
    this.#scheduleVisibilityCheck();
  }

  #handleScrollContainerChange = () => {
    if (this.isConnected) this.#setupIntersectionObserver();
  };

  #scheduleVisibilityCheck = () => {
    if (!this.isConnected || this.#visibilityFrame !== null) return;
    this.#visibilityFrame = requestAnimationFrame(() => {
      this.#visibilityFrame = null;
      if (!this.isConnected || !this.#buyButtonsBlock?.isConnected) return;
      const viewport = this.#visibilityRoot?.getBoundingClientRect();
      const viewportTop = viewport?.top ?? 0;
      const viewportBottom = viewport?.bottom ?? window.innerHeight;
      const buyRect = this.#buyButtonsBlock.getBoundingClientRect();
      const passedBuyButtons = buyRect.bottom < viewportTop;
      // Only inspect the footer when the sticky bar could otherwise appear.
      const footerRect = passedBuyButtons ? this.#footer?.getBoundingClientRect() : null;
      this.#hiddenByBottom = !!footerRect && footerRect.top < viewportBottom && footerRect.bottom > viewportTop - 200;
      const shouldShow = passedBuyButtons && !this.#hiddenByBottom && !this.#isChatActive();
      if (shouldShow && !this.#isStuck) this.#showStickyBar();
      else if (!shouldShow && this.#isStuck) this.#hideStickyBar();
    });
  };

  // Public action handlers
  /**
   * Delegate the single size option to the real picker. Its product-select
   * promise remains the source of truth for the variant, availability and price.
   * @param {Event} event
   */
  handleSizeChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-sticky-size-select]') || !this.contains(select)) {
      return;
    }

    const option = select.options[select.selectedIndex];
    const picker = this.#getVariantPicker();
    const nativeOption = Array.from(picker?.querySelectorAll('[data-option-value-id]') ?? []).find(
      (item) => item instanceof HTMLElement && item.dataset.optionValueId === option?.dataset.optionValueId
    );
    const control = nativeOption instanceof HTMLOptionElement ? nativeOption.closest('select') : nativeOption;

    if (
      !option || option.disabled || !option.value || !picker ||
      typeof /** @type {any} */ (picker).variantChanged !== 'function' ||
      !(nativeOption instanceof HTMLElement) || nativeOption.dataset.variantId !== option.value ||
      (!(control instanceof HTMLInputElement) && !(control instanceof HTMLSelectElement)) || control.disabled
    ) {
      ++this.#variantUpdateVersion;
      clearTimeout(this.#variantUpdateTimeout);
      this.#setVariantState('error');
      return;
    }

    this.#setVariantState('loading');
    const previousVersion = this.#variantUpdateVersion;
    if (control instanceof HTMLInputElement) control.checked = true;
    else control.value = /** @type {HTMLOptionElement} */ (nativeOption).value;
    // No click/focus: changing size must leave the shopper in the sticky bar.
    control.dispatchEvent(new Event('change', { bubbles: true }));
    if (this.#variantUpdateVersion === previousVersion) {
      ++this.#variantUpdateVersion;
      clearTimeout(this.#variantUpdateTimeout);
      this.#setVariantState('error');
    }
  };

  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (this.#variantUpdating || this.#variantUpdateFailed || this.refs.addToCartButton.disabled) return;
    const productForm = this.#getProductForm();
    this.#targetAddToCartButton = productForm?.querySelector('[ref="addToCartButton"]') ?? null;
    if (!this.#targetAddToCartButton || this.#targetAddToCartButton.disabled) return;
    if (!this.#matchesConfirmedVariant(this.dataset.currentVariantId ?? '')) {
      this.#setVariantState('error');
      return;
    }
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target !== this.#getVariantPicker()) return;

    const version = ++this.#variantUpdateVersion;
    const select = this.#getSizeSelect();
    const requestedVariantId = select ? String(event.detail?.variantId ?? '') : '';
    if (select && requestedVariantId) select.value = requestedVariantId;
    this.#setVariantState('loading');
    clearTimeout(this.#variantUpdateTimeout);
    this.#variantUpdateTimeout = setTimeout(() => {
      if (version !== this.#variantUpdateVersion) return;
      ++this.#variantUpdateVersion;
      this.#setVariantState('error');
    }, 20000);

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(async ({ detail }) => {
        // Let all native product-form/price listeners finish their synchronous
        // morphs, regardless of the order in which components were connected.
        await Promise.resolve();
        if (!this.isConnected || version !== this.#variantUpdateVersion) return;
        if (!detail?.html) throw new Error('Missing variant response');

        const { html, productId, resource: variant } = detail;

        if (productId && String(productId) !== this.dataset.productId) throw new Error('Unexpected product response');
        const variantId = variant?.id ? String(variant.id) : '';
        if (variantId && requestedVariantId && variantId !== requestedVariantId) {
          throw new Error('Unexpected size response');
        }
        if (variantId && !this.#matchesConfirmedVariant(variantId)) {
          throw new Error('The native form has not confirmed this size');
        }

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) throw new Error('Missing sticky variant response');

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) throw new Error('Missing sticky bar response');

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;
        const focusedSelect = this.#getSizeSelect();
        const restoreSizeFocus = focusedSelect != null && document.activeElement === focusedSelect;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });
        this.updatedCallback();

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variantId;
        }
        const updatedSelect = this.#getSizeSelect();
        if (updatedSelect && variantId) updatedSelect.value = variantId;
        if (restoreSizeFocus && currentStuck === 'true' && updatedSelect && document.activeElement !== updatedSelect) {
          updatedSelect.focus({ preventScroll: true });
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
          this.dataset.variantAvailable = 'false';
        }
        clearTimeout(this.#variantUpdateTimeout);
        this.#setVariantState('ready');
        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (!this.isConnected || version !== this.#variantUpdateVersion) return;
        clearTimeout(this.#variantUpdateTimeout);
        this.#setVariantState('error');
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /** @returns {HTMLSelectElement | null} */
  #getSizeSelect() {
    return this.querySelector('[data-sticky-size-select]');
  }

  /** @returns {HTMLElement | null} */
  #getVariantPicker() {
    return this.closest('.shopify-section')?.querySelector(
      `variant-picker[ref="mainVariantPicker"][data-product-id="${this.dataset.productId}"]`
    ) ?? null;
  }

  /** @param {string} variantId */
  #matchesConfirmedVariant(variantId) {
    const formVariant = /** @type {HTMLInputElement | null} */ (
      this.#getProductForm()?.querySelector('input[name="id"]')
    );
    if (!variantId || formVariant?.value !== variantId) return false;
    const select = this.#getSizeSelect();
    // Products with several option axes retain their native display and form.
    if (!select) return true;
    const nativeOption = /** @type {HTMLInputElement | HTMLOptionElement | null} */ (
      this.#getVariantPicker()?.querySelector('input[data-option-value-id]:checked, option[data-option-value-id]:checked')
    );
    return select.value === variantId && nativeOption?.dataset.variantId === variantId;
  }

  /** @param {'loading' | 'error' | 'ready'} state */
  #setVariantState(state) {
    this.#variantUpdating = state === 'loading';
    this.#variantUpdateFailed = state === 'error';
    this.refs.stickyBar.setAttribute('aria-busy', String(this.#variantUpdating));
    this.refs.addToCartButton.disabled = state !== 'ready' || this.dataset.variantAvailable !== 'true' ||
      !this.#targetAddToCartButton || this.#targetAddToCartButton.disabled;
    const status = this.querySelector('[data-sticky-size-status]');
    const select = this.#getSizeSelect();
    if (status instanceof HTMLElement) {
      status.hidden = state === 'ready';
      status.textContent = state === 'loading'
        ? select?.dataset.loadingText || window.GlintI18n.t('product.size_updating')
        : state === 'error' ? select?.dataset.errorText || window.GlintI18n.t('product.size_update_error') : '';
    }
  }

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    // Keep the interactive control available to choose a different size.
    if (this.#getSizeSelect()) return;
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    stickyBar.inert = false;
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
    stickyBar.inert = true;
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
